import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, ExternalLink, Plus, Sparkles } from "lucide-react";

function productText(p) {
  const lines = [`Nom : ${p.name}`];
  if (p.category) lines.push(`Catégorie : ${p.category}`);
  if (p.price_chf != null) lines.push(`Prix : CHF ${p.price_chf.toFixed(2)}`);
  lines.push(`Description : ${p.description}`);
  lines.push(`Bouton : ${p.cta}`);
  return lines.join("\n");
}

export function GbpSuggestionsPanel({ suggestions, usedNames, onAddService }) {
  const [copied, setCopied] = useState(null);
  const { services = [], products = [] } = suggestions || {};

  const copy = async (p, i) => {
    try {
      await navigator.clipboard.writeText(productText(p));
      setCopied(i);
      toast.success(`Fiche produit « ${p.name} » copiée — collez-la dans Google Business Profile → Produits.`);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      toast.error("Copie impossible dans ce navigateur.");
    }
  };

  return (
    <div className="border border-[#002FA7]/30 bg-blue-50/40 rounded-lg p-5 space-y-5" data-testid="gbp-suggestions-panel">
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-1">
          <Sparkles className="w-4 h-4 text-[#002FA7]" /> Services proposés par l'IA
          <span className="text-xs font-normal text-slate-500">— ajoutez-les à la liste, puis « Enregistrer sur Google »</span>
        </div>
        {services.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="gbp-suggest-services-empty">Aucun nouveau service proposé (tout est déjà sur la fiche).</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {services.map((s, i) => {
              const used = usedNames.has(s.display_name.trim().toLowerCase());
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-md px-3 py-2.5 flex items-start gap-2" data-testid={`gbp-suggest-service-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                      {s.display_name}
                      {s.service_type_id && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Prédéfini Google</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">{s.description}</p>
                    {s.price_hint && <p className="text-[11px] text-slate-500 mt-0.5">Prix : {s.price_hint}</p>}
                  </div>
                  <button
                    onClick={() => onAddService(s)}
                    disabled={used}
                    data-testid={`gbp-suggest-add-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-medium rounded-md px-2 py-1 border border-[#002FA7]/40 text-[#002FA7] hover:bg-blue-50 disabled:opacity-50 disabled:cursor-default flex-shrink-0"
                  >
                    {used ? <><Check className="w-3.5 h-3.5" /> Ajouté</> : <><Plus className="w-3.5 h-3.5" /> Ajouter</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-1">
          Fiches produits prêtes à coller
          <span className="text-xs font-normal text-slate-500">— Google n'offre pas d'API Produits : copiez chaque fiche puis collez-la dans votre profil</span>
          <a
            href="https://business.google.com/"
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
            data-testid="gbp-open-google-link"
          >
            Ouvrir Google Business <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        {products.length === 0 ? (
          <p className="text-xs text-slate-500">Aucune fiche produit proposée.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {products.map((p, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md px-3 py-2.5" data-testid={`gbp-suggest-product-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {p.category}{p.price_chf != null ? ` · CHF ${p.price_chf.toFixed(2)}` : ""} · bouton « {p.cta} »
                    </div>
                  </div>
                  <button
                    onClick={() => copy(p, i)}
                    data-testid={`gbp-suggest-copy-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-medium rounded-md px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 flex-shrink-0"
                  >
                    {copied === i ? <><Check className="w-3.5 h-3.5 text-green-600" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
                  </button>
                </div>
                <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-line">{p.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
